import * as P230Messages from "../misc/api/DeviceLinkApi/P230In2itiveMessages";
import { DiarySessionCollection, GetAlerts, GetCompliance, GetSession, GetSubject, isIn2itiveError, ClearMemory, RestartWithWait, ReConnectToDevice, GetHomeSpirometrySessions, GetHomeSpirometrySessionAnalysis, GetHomeSpirometryTestAnalysis } from '../misc/api/DeviceLinkApi/P230In2itive';
import { Device } from '../misc/api/DeviceLinkApi/Devices';
import { DiaryPackageDeployment, DiarySession, QueryOfDiaryTest, QueryOfQuestionnaireTestAnswer, QuestionnaireTestAnswer, WebPortalClient, SubjectCreation, QueryOfFvcTest } from "webportalclient";
import { DevicesAction, DevicesState, SetDeviceOverallUploadStatusAction, UploadStatus, SetUploadStatusAction, SetInteractionModalStepAction } from './DevicesNewReducer';
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { QueryOfFvcAnalysisSetTest } from "webportalclient";
import { NIL } from 'uuid';
import { isEmpty } from "lodash";


export class UploadDiarySessionsAsyncAction {
    public static async UploadSingle(client: WebPortalClient, currentStudy: string, device: Device, currentDeviceSubject: SubjectCreation, packageDeployments: DiaryPackageDeployment[], errorOnEmpty: boolean, onStatusChange: (message: string) => void, onOverallStatusChange: (status: UploadStatus, message?: string) => void): Promise<UploadStatus> {
        const CheckValidSubject = async (currentDeviceSubject: SubjectCreation, client: WebPortalClient): Promise<boolean> => {
            try {
                const subject = await client.getSubjects(undefined, `id eq ${currentDeviceSubject.id} and site/study/id eq ${currentStudy}`, undefined, "site($expand=study)");
                if (subject.value.length > 0)
                    return true;
                else
                    return false;
            } catch {
                onOverallStatusChange(UploadStatus.Failed, P230Messages.SubjectForAnotherStudyAllocatedOnDevice(currentDeviceSubject.number));
                return false;
            }
        }

        try {
            if (!await CheckValidSubject(currentDeviceSubject, client))
                throw new Error(P230Messages.SubjectForAnotherStudyAllocatedOnDevice(currentDeviceSubject.number));

            const result = await GetSession(device.id);

            if (result === undefined)
                return UploadStatus.Uploaded;

            let uploadResult = UploadStatus.Uploading;

            if (isIn2itiveError(result)) {
                onStatusChange(result.error.message);
                return UploadStatus.PartialFailed;
            }

            // HACK: For eDiary version 1.05.0040
            result.eDiarySessions = result.eDiarySessions.filter(x => x.id !== NIL);
            // ENDHACK

            const diarySessions: DiarySession[] = result.eDiarySessions.map(s => {
                return {
                    id: s.id,
                    visitId: s.visitId,
                    name: s.name,
                    occurred: s.occurred,
                    batteryLevel: s.batteryLevel,
                    applicationVersion: s.applicationVersion,
                    serialNumber: s.serialNumber,
                    pefRepeatable: s.pefRepeatable,
                    pefRepeatability: s.pefRepeatability,
                    tests: s.tests ? s.tests.map(t => {
                        return {
                            id: undefined,
                            sessionId: s.id,
                            occurred: t.occurred,
                            index: t.index,
                            accepted: t.accepted,
                            qualityIndicators: t.qualityIndicators,
                            pef: t.pef,
                            fev1: t.fev1
                        }
                    }) : undefined,
                    questionnaires: s.questionnaires ? s.questionnaires.map(q => {
                        return {
                            id: q.id,
                            sessionId: s.id,
                            questionnaireId: q.questionnaireId,
                            score: q.score,
                            occurred: q.occurred,
                            name: q.name,
                            answers: q.answers ? q.answers.map(a => {
                                return {
                                    id: undefined,
                                    questionnaireTestId: q.id,
                                    questionIndex: a.questionIndex.toString(),
                                    answerIndex: a.answerIndex.toString(),
                                    answerWeight: a.answerWeight,
                                    answer: a.answer,
                                }
                            }) : undefined
                        }
                    }) : undefined
                } as DiarySession;
            });

            for (const session of diarySessions) {
                if (!isEmpty(session.name)) {
                    const deployment = packageDeployments.find(pd => pd.sessionSchedule.label === session.name);
                    if (deployment.sessionSchedule.sessionTypeId === SessionTypes.EDiary) {
                        onStatusChange(`Uploading session ${diarySessions.indexOf(session) + 1}  of ${diarySessions.length}`);
                        let existingSession;
                        try {
                            existingSession = await client.getDiarySession(session.id);
                        }
                        catch {
                            existingSession = null;
                        }

                        if (!existingSession) {
                            try {
                                await client.postDiarySession(session);
                            } catch {
                                onStatusChange(`Failed to upload session '${session.id}'`);
                                return UploadStatus.Failed;
                            }
                        }
                    }

                    if (session.tests) {
                        for (const test of session.tests) {
                            let existingTest: QueryOfDiaryTest;
                            try {
                                existingTest = await client.getDiaryTests(undefined, `sessionId eq ${session.id} and index eq ${test.index}`);
                            }
                            catch {
                                existingTest = { value: [] };
                            }

                            if (existingTest.value.length === 0) {
                                try {
                                    await client.postDiaryTest(test);
                                } catch {
                                    onStatusChange(`Failed to upload test '${test.id}'`);
                                    return UploadStatus.Failed;
                                }
                            }
                        }
                    }

                    if (session.questionnaires) {
                        for (const questionnaire of session.questionnaires) {
                            let existingQuestionnaire;

                            try {
                                existingQuestionnaire = await client.getQuestionnaireTest(questionnaire.id);
                            } catch {
                                existingQuestionnaire = null;
                            }

                            if (!existingQuestionnaire) {
                                try {
                                    const questionnaireMutation = { ...questionnaire, answers: undefined };
                                    existingQuestionnaire = await client.postQuestionnaireTest(questionnaireMutation)
                                } catch {
                                    onStatusChange(`Failed to upload questionnaire for session '${session.id}'`);
                                    return UploadStatus.Failed;
                                }
                            }
                            if (questionnaire.answers) {
                                for (const answer of questionnaire.answers) {
                                    let existingAnswer: QueryOfQuestionnaireTestAnswer;

                                    try {
                                        existingAnswer = await client.getQuestionnaireTestAnswers(undefined, `questionnaireTestId eq ${existingQuestionnaire.id} and questionIndex eq '${answer.questionIndex}'`)
                                    } catch {
                                        existingAnswer = { value: [] };
                                    }

                                    if (existingAnswer.value.length === 0) {
                                        try {
                                            const answerMutation: QuestionnaireTestAnswer = { ...answer, questionnaireTestId: existingQuestionnaire.id };
                                            await client.postQuestionnaireTestAnswer(answerMutation);
                                        } catch {
                                            onStatusChange(`Failed to upload questionnaire answers for session '${session.id}'`);
                                            return UploadStatus.Failed;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            const homeSpirometryUploadResult = await UploadDiarySessionsAsyncAction.uploadHomeSpirometry(client, device.id, onStatusChange);
            uploadResult = homeSpirometryUploadResult ? homeSpirometryUploadResult : uploadResult;

            const alertUploadResult = await UploadDiarySessionsAsyncAction.uploadAlerts(client, device.id, result, packageDeployments[0], onStatusChange);    // HACK: Need visit Id from device
            uploadResult = alertUploadResult ? alertUploadResult : uploadResult;

            const complianceUploadResult = await UploadDiarySessionsAsyncAction.uploadCompliance(client, device.id, onStatusChange);
            uploadResult = complianceUploadResult ? complianceUploadResult : uploadResult;
            return uploadResult === UploadStatus.Uploading ? UploadStatus.Uploaded : uploadResult;
        } catch (err) {
            onStatusChange(err.message ? err.message : P230Messages.FailedToUploadMessage);
            onOverallStatusChange(UploadStatus.Failed, err.message ? err.message : P230Messages.FailedToUploadMessage);
            return UploadStatus.Failed;
        }
    }

    private static async uploadAlerts(client: WebPortalClient, deviceId: string, session: DiarySessionCollection, packageDeployment: DiaryPackageDeployment, onStatusChange: (message: string) => void) {
        const result = await GetAlerts(deviceId);

        if (result === undefined)
            return UploadStatus.Uploaded;

        if (isIn2itiveError(result)) {
            onStatusChange(result.error.message);
            return UploadStatus.PartialFailed;
        }

        for (const alert of result.alerts) {
            let existingAlertResult;
            try {
                existingAlertResult = await (await client.getAlerts(undefined, `id eq ${alert.id}`)).value;
            }
            catch {
                existingAlertResult = [];
            }
            try {
                if (existingAlertResult.length === 0) {
                    await client.postAlert({
                        ...alert,
                        alertTemplateId: "Vitalograph.WebPortal.Modules.Alerts.In2itiveEDiaryCatchAllAlerts",
                    });
                }
            } catch {
                onStatusChange(`Failed to upload alert '${alert.headline}' for session '${alert.triggeredBySessionId}'`);
                return UploadStatus.Failed;
            }
        }
    }

    private static async uploadCompliance(client: WebPortalClient, deviceId: string, onStatusChange: (message: string) => void) {
        const result = await GetCompliance(deviceId);

        if (result === undefined)
            return UploadStatus.Uploaded;

        if (isIn2itiveError(result)) {
            onStatusChange(result.error.message);
            return UploadStatus.PartialFailed;
        }

        for (const compliance of result.compliances) {
            let existingComplianceResult;
            try {
                existingComplianceResult = await (await client.getDiaryCompliances(undefined, `id eq ${compliance.sessionId} and eDiarySessionComplianceIdentifier eq ${compliance.eDiarySessionComplianceIdentifier} and complianceType ne 4`)).value;
            }
            catch {
                existingComplianceResult = [];
            }

            try {
                if (existingComplianceResult.length === 0) {
                    await client.postDiaryCompliance(compliance);
                } else {
                    await client.patchDiaryCompliance(compliance, compliance.sessionId, "");
                }
            } catch {
                onStatusChange(`Failed to upload compliance information for session '${result.compliances[0].sessionId}'`);
                return UploadStatus.Failed;
            }
        }
    }

    private static async uploadHomeSpirometry(client: WebPortalClient, deviceId: string, onStatusChange: (message: string) => void) {
        const result = await GetHomeSpirometrySessions(deviceId);
        const analysisSets = await GetHomeSpirometrySessionAnalysis(deviceId);
        const analysisSetTests = await GetHomeSpirometryTestAnalysis(deviceId);

        if (result === undefined)
            return UploadStatus.Uploaded;

        if (isIn2itiveError(result)) {
            onStatusChange(result.error.message);
            return UploadStatus.PartialFailed;
        }
        for (const session of result.fvcSessions) {
            let existingSession;
            try {
                existingSession = await client.getFvcSession(session.id);
            } catch {
                existingSession = null;
            }

            if (!existingSession) {
                try {
                    await client.postFvcSession(session);
                } catch (ex) {
                    onStatusChange(`Failed to upload home spirometry session '${session.id}'`);
                    return UploadStatus.Failed;
                }
            }

            const analysisSetFromDiary = analysisSets.fvcAnalysisSet.find(fa => fa.sessionId === session.id);

            let existingAnalysisSet;
            try {
                existingAnalysisSet = await client.getSpirometryAnalysisSet(analysisSetFromDiary.id);
            } catch {
                existingAnalysisSet = null;
            }

            if (!existingAnalysisSet) {
                try {
                    await client.postSpirometryAnalysisSet(analysisSetFromDiary);
                } catch {
                    onStatusChange(`Failed to upload home spirometry analysis set '${session.id}'`);
                    return UploadStatus.Failed;
                }
            }

            if (session.tests) {
                for (const test of session.tests) {
                    let existingTest: QueryOfFvcTest;
                    try {
                        existingTest = await client.getFvcTests(undefined, `sessionId eq ${session.id} and index eq ${test.index}`);
                    }
                    catch {
                        existingTest = { value: [] };
                    }

                    if (existingTest.value.length === 0) {
                        try {
                            await client.postFvcTest(test);
                        } catch {
                            onStatusChange(`Failed to upload home spirometry test '${test.id}'`);
                            return UploadStatus.Failed;
                        }
                    }

                    let existingAnalysisSetTests: QueryOfFvcAnalysisSetTest;
                    try {
                        existingAnalysisSetTests = await client.getSpirometryAnalysisSetTests(undefined, `analysisSetId eq ${existingAnalysisSet.id} and testId eq ${test.id}`);
                    }
                    catch {
                        existingAnalysisSetTests = { value: [] };
                    }

                    if (existingAnalysisSetTests.value.length === 0) {
                        try {
                            const analysisSetTestFromDiary = analysisSetTests.fvcAnalysisSetTest.find(fast => fast.analysisSetId === analysisSetFromDiary.id && fast.testId === test.id);
                            if (analysisSetTestFromDiary) {
                                await client.postSpirometryAnalysisSetTest(analysisSetTestFromDiary);
                            }
                        } catch (ex) {
                            onStatusChange(`Failed to upload home spirometry analysis set test '${test.id}'`);
                            return UploadStatus.Failed;
                        }
                    }
                }
            }
        }
    }

    private static async resetDeviceOnCompletion(deviceId: string, onStatusChange: (message: string) => void) {
        onStatusChange(P230Messages.ClearingDevice);
        const clearResult = await ClearMemory(deviceId);
        if (!clearResult) {
            return onStatusChange(P230Messages.FailedToClear);
        }

        await RestartWithWait(deviceId);

        await ReConnectToDevice();
    }

    public static async Upload(client: WebPortalClient, dispatch: React.Dispatch<DevicesAction>, state: DevicesState, currentStudyId: string) {
        const allUploadResults: UploadStatus[] = [];

        for (const device of state.selectedDevices) {
            let uploadResult = UploadStatus.Uploading;
            dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Uploading));
            dispatch(SetUploadStatusAction.create(P230Messages.CheckingForData));

            let resetOnCompletion = false;
            for (const id of state.upload.selectedSessionIds) {
                const visit = state.upload.parentVisitCandidates.find(v => v.id === id);

                const packageDeployments = await client.getDiaryPackageDeployments(undefined, `serialNumber eq '${device.serial}' and visitId eq ${visit.id}`, undefined, "sessionSchedule");
                const sessionSchedulesIds = packageDeployments.value.filter(pd => pd.visitId === id).map(m => m.sessionScheduleId)

                const studyScheduleSessions = await client.getStudyScheduleSessions("id,resetDiaryOnUpload", `id in (${sessionSchedulesIds.join(',')})`);

                if (studyScheduleSessions.value.some(ss => ss.resetDiaryOnUpload)) {
                    resetOnCompletion = true;
                }

                const subjectResult = await GetSubject(device.id);

                if (!subjectResult) {
                    uploadResult = UploadStatus.Failed;
                    dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Failed, P230Messages.NoSubjectOnDevice));
                }

                if (subjectResult) {
                    uploadResult = await this.UploadSingle(
                        client,
                        currentStudyId,
                        device,
                        subjectResult.subject,
                        packageDeployments.value.filter(pd => pd.visitId === id),
                        true,
                        (message) => dispatch(SetUploadStatusAction.create(message)),
                        (status, message) => dispatch(SetDeviceOverallUploadStatusAction.create(device.id, status, message))
                    );
                }
            }

            if (resetOnCompletion) {
                await UploadDiarySessionsAsyncAction.resetDeviceOnCompletion(device.id, (message) => dispatch(SetUploadStatusAction.create(message)));
            }

            allUploadResults.push(uploadResult);
            dispatch(SetDeviceOverallUploadStatusAction.create(device.id, uploadResult));
        }


        if (allUploadResults.some(r => r === UploadStatus.Failed)) {
            dispatch(SetInteractionModalStepAction.create(5));
        } else if (allUploadResults.some(r => r === UploadStatus.PartialFailed)) {
            dispatch(SetInteractionModalStepAction.create(5));
        } else {
            dispatch(SetUploadStatusAction.create(P230Messages.UploadComplete));
            dispatch(SetInteractionModalStepAction.create(4));
        }
    }
}
